import React, { useRef } from 'react';
import useSeo from '../lib/seo';
import { Link } from 'react-router-dom';
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { useGSAP } from '@gsap/react';
import { ArrowRight } from 'lucide-react';
import PageHero from '../components/site/PageHero';
import { RING_PATH } from '../components/home/v3/LogoMark';

gsap.registerPlugin(ScrollTrigger, useGSAP);

const LEAD =
  'A Idealizer nasceu de um sonho. Em meados de 2023, em um cenário de instabilidade global, pós-pandemia, onde tudo mudava rápido demais, três jovens estudantes de Ciência da Computação decidiram seguir um caminho diferente.';

const VALUES = [
  { label: 'Missão', text: 'Entregar soluções tecnológicas que transformem negócios.' },
  { label: 'Visão', text: 'Ser referência em inovação e tecnologia no mercado.' },
  { label: 'Valores', text: 'Compromisso, Inovação, Ética e Excelência.' },
];

function Sobre() {
  const root = useRef(null);

  useSeo({
    title: 'Sobre | Idealizer',
    description: 'Conheça a Idealizer, estúdio de produtos digitais fundado em 2023 que desenha, desenvolve e publica apps e plataformas web.',
    path: '/sobre',
  });

  useGSAP(
    () => {
      const q = gsap.utils.selector(root);
      const mm = gsap.matchMedia();
      mm.add('(prefers-reduced-motion: no-preference)', () => {
        // Abertura da história: cada palavra acende com a leitura.
        gsap.fromTo(
          q('.ab-w'),
          { opacity: 0.14 },
          { opacity: 1, ease: 'none', stagger: 0.1, scrollTrigger: { trigger: q('.ab-lead')[0], start: 'top 78%', end: 'bottom 45%', scrub: 0.4 } }
        );
        gsap.utils.toArray(q('.ab-story p, .ab-quote, .ab-closing')).forEach((el) => {
          gsap.from(el, { y: 40, autoAlpha: 0, duration: 1.1, ease: 'expo.out', scrollTrigger: { trigger: el, start: 'top 86%' } });
        });
        gsap.from(q('.ab-value'), {
          yPercent: 30,
          autoAlpha: 0,
          duration: 1.1,
          ease: 'expo.out',
          stagger: 0.1,
          scrollTrigger: { trigger: q('.ab-values')[0], start: 'top 80%' },
        });
        // Propósito: o anel se fecha em volta da frase conforme a seção entra.
        const ring = q('.ab-purpose-ring path')[0];
        const len = ring.getTotalLength();
        gsap.fromTo(
          ring,
          { strokeDasharray: len, strokeDashoffset: len },
          { strokeDashoffset: 0, ease: 'none', scrollTrigger: { trigger: q('.ab-purpose')[0], start: 'top 80%', end: 'center 50%', scrub: 0.6 } }
        );
        gsap.fromTo(
          q('.ab-purpose-ring'),
          { scale: 0.8, rotate: -60 },
          { scale: 1, rotate: 0, ease: 'none', scrollTrigger: { trigger: q('.ab-purpose')[0], start: 'top bottom', end: 'center 50%', scrub: true } }
        );
      });
      return () => mm.revert();
    },
    { scope: root }
  );

  return (
    <main className="pg about-v3" ref={root}>
      <PageHero
        lines={['Nós somos', { text: 'a Idealizer.', accent: true }]}
        lede="Um estúdio de produtos digitais que desenha, desenvolve e coloca no ar apps e plataformas, do primeiro esboço à loja."
      />

      <section className="container ab-intro" aria-label="Como começamos">
        <p className="ab-lead">
          {LEAD.split(' ').map((w, i) => (
            <span className="ab-w" key={i}>
              {w}{' '}
            </span>
          ))}
        </p>
      </section>

      <section className="container ab-history" aria-labelledby="historia-title">
        <div className="ab-year" aria-hidden>
          <span>2023</span>
        </div>
        <div className="ab-story">
          <h2 id="historia-title" className="ab-h2">
            Nossa história
          </h2>
          <p>
            Enquanto muitos buscavam segurança em carreiras tradicionais, eles queriam criar. Queriam inovar. Queriam
            transformar.
          </p>
          <p>
            Entre salas de aula, projetos experimentais e madrugadas explorando tecnologias emergentes, surgiu a ideia que
            se tornaria a base da Idealizer: construir soluções digitais com impacto real para pessoas, para empresas e
            para o mercado.
          </p>
          <p>
            Os fundadores compartilhavam a mesma visão: desenvolver produtos modernos, intuitivos e escaláveis usando o
            que há de mais atual em tecnologia. Desde o início, o foco esteve em criar experiências capazes de gerar
            conversão, retenção e valor contínuo. Não bastava apenas programar; era preciso resolver problemas,
            simplificar rotinas e criar ferramentas que fizessem diferença.
          </p>
          <p>Com esse propósito, a Idealizer ganhou vida.</p>
          <p>
            Hoje, a empresa atua no desenvolvimento de aplicativos iOS e Android, plataformas web, sistemas de
            assinatura, dashboards inteligentes, integrações complexas com APIs, automações, produtos completos baseados
            em Next.js, React, Expo, Node.js e muito mais. Cada projeto é construído com dedicação, estratégia e uma
            atenção profunda à experiência do usuário.
          </p>
          <blockquote className="ab-quote">
            <span>A essência continua a mesma:</span> transformar ideias em soluções digitais que impactam o mercado e
            contribuem positivamente para a vida das pessoas.
          </blockquote>
          <p className="ab-closing">
            A Idealizer é mais do que uma empresa de tecnologia. É um movimento. É a evolução da criatividade guiada pela
            engenharia. É o resultado da coragem de três jovens que escolheram inovar quando o mundo inteiro estava
            mudando.
          </p>
          <p>
            E hoje, continuamos avançando, criando, idealizando sempre de olho no futuro e no impacto que podemos gerar.
          </p>
        </div>
      </section>

      <section className="container ab-values" aria-labelledby="valores-title">
        <h2 id="valores-title" className="ab-h2">
          No que acreditamos
        </h2>
        <ul>
          {VALUES.map((v) => (
            <li className="ab-value" key={v.label}>
              <span className="ab-value-label">{v.label}</span>
              <p className="ab-value-text">{v.text}</p>
            </li>
          ))}
        </ul>
      </section>

      <section className="ab-purpose" aria-labelledby="proposito-title">
        <svg className="ab-purpose-ring" viewBox="26 26 162 162" aria-hidden focusable="false">
          <path d={RING_PATH} fill="none" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" vectorEffect="non-scaling-stroke" />
        </svg>
        <div className="container ab-purpose-inner">
          <h2 id="proposito-title" className="ab-purpose-title">
            Conectar pessoas e tecnologias para um futuro melhor.
          </h2>
          <p className="ab-purpose-text">
            Trabalhamos para que a tecnologia esteja acessível a todos, promovendo a transformação digital nas empresas e
            oferecendo soluções que realmente fazem a diferença.
          </p>
          <Link to="/contato" className="btn-primary">
            Começar um projeto <ArrowRight size={18} strokeWidth={2} aria-hidden />
          </Link>
        </div>
      </section>
    </main>
  );
}

export default Sobre;
